import React, { useEffect, useState } from "react";
import { api, formatRM } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const fmtAdd = (c) => { const a = Number(c.add_on); if (!a) return ""; const t = c.add_on_type || "per_unit"; return t === "percent" ? ` (+${a}%)` : ` (${a > 0 ? "+" : "−"}RM${Math.abs(a).toFixed(2)}${t === "fixed" ? " once" : t === "per_sqft" ? "/sqft" : ""})`; };
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

// Renders spec fields + finishing chips for a product and keeps a live quote.
export const ProductConfigurator = ({ product, value, onChange, quoteUrl, quantity, processTime, deliveryMethod, onQuote, testid = "cfg", hideDelivery = false }) => {
  const { specs = {}, finishing_sel = [] } = value;
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!product) { setQuote(null); onQuote?.(null); return; }
    const t = setTimeout(() => {
      api.post(quoteUrl, { product_id: product.id, specs, finishing_sel, delivery_method: deliveryMethod || null, quantity: Number(quantity) || 0, process_time: processTime, customer_name: "-" })
        .then((r) => { setQuote(r.data); onQuote?.(r.data); }).catch(() => { setQuote(null); onQuote?.(null); });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [product?.id, JSON.stringify(specs), JSON.stringify(finishing_sel), deliveryMethod, quantity, processTime]);

  if (!product) return null;
  const setSpec = (k, v) => onChange({ specs: { ...specs, [k]: v }, finishing_sel });
  const toggleFin = (name, group) => {
    const others = (group.choices || []).map((c) => c.name).filter((n) => n !== name);
    const base = finishing_sel.filter((n) => !others.includes(n));
    onChange({ specs, finishing_sel: base.includes(name) ? base.filter((n) => n !== name) : [...base, name] });
  };
  const errors = quote?.errors || [];

  return (
    <>
      {(product.specs || []).map((g) => (
        <div key={g.label}>
          <Label>{g.label}{g.required !== false ? " *" : ""}{g.type === "number" && g.unit ? ` (${g.unit})` : ""}</Label>
          {g.type === "number" ? (
            <Input type="number" step="any" min={g.min ?? undefined} max={g.max ?? undefined} value={specs[g.label] ?? ""} onChange={(e) => setSpec(g.label, e.target.value)} data-testid={`${testid}-spec-${slug(g.label)}`} />
          ) : (
            <Select value={specs[g.label] || "__none"} onValueChange={(v) => setSpec(g.label, v === "__none" ? "" : v)}>
              <SelectTrigger data-testid={`${testid}-spec-${slug(g.label)}`}><SelectValue placeholder={`Select ${g.label}`} /></SelectTrigger>
              <SelectContent><SelectItem value="__none">— Select —</SelectItem>{(g.choices || []).map((c) => <SelectItem key={c.name} value={c.name}>{c.name}{fmtAdd(c)}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      ))}
      {(product.finishing || []).length > 0 && (
        <div className="md:col-span-2 space-y-2" data-testid={`${testid}-finishing`}>
          <Label>Finishing</Label>
          {(product.finishing || []).map((g) => (
            <div key={g.label} className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground w-24 shrink-0">{g.label}{g.required ? " *" : ""}</span>
              {(g.choices || []).map((c) => { const on = finishing_sel.includes(c.name); return (
                <button type="button" key={c.name} onClick={() => toggleFin(c.name, g)} data-testid={`${testid}-fin-${slug(c.name)}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-cyan-600 border-cyan-700 text-white font-semibold" : "hover:bg-muted"}`}>{c.name}{fmtAdd(c)}</button>); })}
            </div>
          ))}
        </div>
      )}
      {quote?.area_sqft > 0 && <div className="md:col-span-2 text-xs text-muted-foreground">Area: <span className="font-mono font-semibold">{quote.area_sqft} sqft</span></div>}
      <div className="md:col-span-2 bg-cyan-50 dark:bg-cyan-950/30 p-3 rounded-lg space-y-1 text-sm" data-testid={`${testid}-summary`}>
        {errors.length > 0 && <ul className="text-rose-600 text-xs list-disc pl-4" data-testid={`${testid}-errors`}>{errors.map((e) => <li key={e}>{e}</li>)}</ul>}
        {quote?.ok && (
          <>
            {(quote.lines || []).map((l, i) => <div key={i} className="flex justify-between text-xs text-muted-foreground"><span>{l.label}{l.per_unit ? " / pc" : ""}</span><span className="font-mono">{l.amount == null ? `+${l.percent}%` : formatRM(l.amount)}</span></div>)}
            <div className="flex justify-between border-t pt-1"><span>Unit price</span><span className="font-mono">{formatRM(quote.unit_price)}</span></div>
            <div className="flex justify-between"><span>Subtotal × {quote.quantity}{quote.fixed_add_on ? ` + fixed RM${quote.fixed_add_on.toFixed(2)}` : ""}</span><span className="font-mono">{formatRM(quote.subtotal)}</span></div>
            {quote.setup_fee > 0 && <div className="flex justify-between"><span>Setup fee</span><span className="font-mono">{formatRM(quote.setup_fee)}</span></div>}
            {quote.surcharge_amount > 0 && <div className="flex justify-between"><span>{processTime} surcharge</span><span className="font-mono">{formatRM(quote.surcharge_amount)}</span></div>}
            {!hideDelivery && <div className="flex justify-between"><span>{deliveryMethod || "Delivery"}{quote.weight_kg > 0 ? <span className="text-xs text-muted-foreground"> ({quote.weight_kg} kg)</span> : null}</span><span className="font-mono">{quote.delivery_fee > 0 ? formatRM(quote.delivery_fee) : "Free"}</span></div>}
          </>
        )}
        <div className="flex items-center justify-between border-t pt-1"><span className="font-semibold">{hideDelivery ? "Item total" : "Total"}</span><span className="font-display font-bold text-2xl font-mono" data-testid={`${testid}-total`}>{formatRM(quote?.ok ? quote.total_price : 0)}</span></div>
      </div>
    </>
  );
};
